import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { LayerTogglePanel } from '../../../maps/LayerTogglePanel';
import { useMapEngine } from '../../../../hooks/useMapEngine';
import { getEphemeralGeojson } from '../../../../services/gisService';
import type { GeoJSONFeatureCollection, MapView } from '../../../../types/gis';
import { computeFeaturesBbox } from '../../../../utils/featureBbox';
import {
  featureCollectionToLayers,
  hasMultipleLayers,
} from '../../../../utils/featureCollectionToLayers';
import { JsonPreviewRenderer } from './JsonPreviewRenderer';

interface RendererProps {
  toolCall: any;
  darkMode: boolean;
  compactMode?: boolean;
  onSaveAsArtifact?: (fc: GeoJSONFeatureCollection, view?: MapView) => Promise<string>;
  onOpenInViewer?: (artifactId: string | null, fc: GeoJSONFeatureCollection, view?: MapView) => void;
}

type LifecycleState = 'ephemeral' | 'saving' | 'saved';

const EMPTY_FC: GeoJSONFeatureCollection = { type: 'FeatureCollection', features: [] };

export const MapInlineRenderer: React.FC<RendererProps> = ({
  toolCall, darkMode, compactMode, onSaveAsArtifact, onOpenInViewer,
}) => {
  // All hooks unconditionally at the top — React rules of hooks.
  const [state, setState] = useState<LifecycleState>('ephemeral');
  const [savedId, setSavedId] = useState<string | null>(null);
  const [hydratedFc, setHydratedFc] = useState<GeoJSONFeatureCollection | null>(null);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  const [hiddenLayers, setHiddenLayers] = useState<ReadonlySet<string>>(() => new Set());

  const result = toolCall?.result || {};
  const success = result.success === true;
  const inlineFc: GeoJSONFeatureCollection | null = success ? (result.geojson || null) : null;
  // Digest envelope: when feature_count > 20 the GIS tool stores the full
  // FC in Redis under this id (TTL 5min) instead of inlining it. We fetch
  // and render. See backend/app/tools/gis/geojson_create.py:171-178 +
  // backend/app/api/routes/maps.py:get_ephemeral_geojson.
  const ephemeralId: string | null = success ? (result.ephemeral_geojson_id || null) : null;
  const fc: GeoJSONFeatureCollection | null = inlineFc || hydratedFc;
  const featureCount = result.feature_count ?? (fc?.features?.length ?? 0);
  const view: MapView | undefined = result.view_hint;

  // Hydrate digest envelope on mount. Cancel-on-unmount guards against a
  // late response setting state on an unmounted component.
  useEffect(() => {
    if (inlineFc || !ephemeralId) return;
    let cancelled = false;
    getEphemeralGeojson(ephemeralId)
      .then((data) => { if (!cancelled) setHydratedFc(data); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const msg = status === 404
          ? 'Map preview expired — re-run the prompt to regenerate.'
          : `Failed to load map preview: ${detail || err?.message || 'unknown error'}`;
        setHydrateError(msg);
      });
    return () => { cancelled = true; };
  }, [inlineFc, ephemeralId]);

  // Engine is mounted unconditionally so React's hook order stays stable
  // across success/failure/compact rerenders. The hook itself is a no-op
  // until containerRef is attached + visible (lazyMount).
  // interactive=true lets the user pan/zoom inside the inline preview —
  // important when the auto-fit doesn't catch every feature (the user
  // can drag/wheel to find the rest without leaving the chat).
  const engine = useMapEngine({
    geojson: fc || EMPTY_FC,
    view,
    interactive: true,
    lazyMount: true,
    preserveDrawingBuffer: false,
    hiddenLayers,
  });

  const showLayerPanel = useMemo(
    () => (fc ? hasMultipleLayers(fc) : false),
    [fc],
  );

  const handleToggleLayer = useCallback((layerName: string) => {
    setHiddenLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerName)) next.delete(layerName);
      else next.add(layerName);
      return next;
    });
  }, []);

  const handleFitToLayer = useCallback((layerName: string) => {
    if (!fc || !engine.mapInstance) return;
    const buckets = featureCollectionToLayers(fc);
    const features = buckets[layerName] || [];
    const bbox = computeFeaturesBbox(features);
    if (!bbox) return;
    engine.mapInstance.fitBounds(bbox, { padding: 30, duration: 600, maxZoom: 14 });
  }, [fc, engine.mapInstance]);

  // Failure path: fall back to JSON preview.
  if (!success) {
    return <JsonPreviewRenderer toolCall={toolCall} darkMode={darkMode} />;
  }

  if (compactMode) {
    return (
      <div
        data-testid="map-inline-collapsed"
        className={`text-xs flex items-center justify-between px-2 py-1 rounded ${
          darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
        }`}
      >
        <span>{featureCount} features · {result.summary || ''}</span>
        <button className="ml-2 underline">expand ▾</button>
      </div>
    );
  }

  const handleSave = async () => {
    if (!onSaveAsArtifact || !fc) return;
    setState('saving');
    try {
      const id = await onSaveAsArtifact(fc, view);
      setSavedId(id);
      setState('saved');
    } catch {
      setState('ephemeral');
    }
  };

  const handleOpen = () => {
    if (onOpenInViewer && fc) {
      onOpenInViewer(savedId, fc, view);
    }
  };

  const hydrating = fc === null && ephemeralId !== null && !hydrateError;

  return (
    <div
      data-testid="map-inline-renderer"
      className={`relative rounded-lg overflow-hidden border ${
        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'
      }`}
    >
      <div ref={engine.containerRef} style={{ width: '100%', height: 280 }} />
      {hydrating && (
        <div
          data-testid="map-inline-loading"
          className={`absolute inset-0 flex items-center justify-center text-sm ${
            darkMode ? 'bg-gray-900/80 text-gray-300' : 'bg-white/80 text-gray-600'
          }`}
        >
          Loading map preview…
        </div>
      )}
      {hydrateError && (
        <div
          data-testid="map-inline-error"
          className={`absolute inset-0 flex items-center justify-center text-sm p-4 text-center ${
            darkMode ? 'bg-gray-900/90 text-red-300' : 'bg-white/90 text-red-700'
          }`}
        >
          {hydrateError}
        </div>
      )}
      {showLayerPanel && fc && (
        <LayerTogglePanel
          fc={fc}
          hiddenLayers={hiddenLayers}
          onToggle={handleToggleLayer}
          onFitToLayer={handleFitToLayer}
          darkMode={darkMode}
        />
      )}
      <div className={`p-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {state === 'saving' && <span>Saving as artifact…</span>}
        {state === 'saved' && (
          <span>✓ Saved as artifact</span>
        )}
        {state === 'ephemeral' && (
          <>
            <span>{featureCount} features · ephemeral preview</span>
            <div className="mt-1 flex gap-2">
              <button
                onClick={handleSave}
                disabled={!fc}
                className="px-2 py-0.5 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Save as artifact
              </button>
              <button
                onClick={handleOpen}
                disabled={!fc}
                className="px-2 py-0.5 text-xs rounded border border-gray-400 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Open in viewer →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
